import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from "react";
import {
  DatePicker, Input, Modal, Select, Table, Button, Flex,
} from "antd";
import { CloseOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { getSearchOptions } from "../../../actions/batchReports";
import { fetchTestCategory } from "../../../actions/chemistry";
import { detectErrorRuleCheck, getWoInfoLogs } from "../../../actions/qc";
import { formatAntdDateRange } from "../../../utils/helpers";
import useAbortController from "../../Common/hooks/useAbortController";
import StatusTag from "../../Common/components/StatusTag";
import QcRuleTestDetailSection from "./QcRuleTestDetailSection";
import "./QcRuleTestModal.css";

const { RangePicker } = DatePicker;

const defaultCategoryOption = { value: "", label: "All Categories" };
const defaultCustomerOption = { value: "", label: "All Customers" };
const defaultPageSize = 50;
const MAX_SELECTED_ROWS = 20;
const AI_QC_COLUMN_WIDTH = 180;
const TEXT_COLUMN_WIDTH = 180;

const renderWrappedCell = (value) => (
  <span className="QcRuleTestModal__WrappedCell">{value || "-"}</span>
);

const getRowKey = (record) => record.id ?? record.tests_in_sample_PK;

const formatSelectedRowLabel = (row) => [
  row.wo_id,
  row.genista_id,
  row.internal_name,
  row.test_category,
].map((value) => value || "-").join("; ");

export const defaultQcRuleTestDateRange = () => [
  dayjs().subtract(30, "day"),
  dayjs(),
];

const baseColumns = [
  {
    title: "Customer ID",
    dataIndex: "customer_id",
    key: "customer_id",
  },
  {
    title: "Workorder ID",
    dataIndex: "wo_id",
    key: "wo_id",
  },
  {
    title: "Genista ID",
    dataIndex: "genista_id",
    key: "genista_id",
  },
  {
    title: "Test Name",
    dataIndex: "internal_name",
    key: "internal_name",
    width: TEXT_COLUMN_WIDTH,
    render: renderWrappedCell,
  },
  {
    title: "Test Category",
    dataIndex: "test_category",
    key: "test_category",
    width: TEXT_COLUMN_WIDTH,
    render: renderWrappedCell,
  },
  {
    title: "Result",
    dataIndex: "result",
    key: "result",
    width: TEXT_COLUMN_WIDTH,
    render: renderWrappedCell,
  },
];

const renderAiQcCell = (qcResult, qcStatus) => (
  <div className="QcRuleTestModal__ResultCell">
    <StatusTag result={qcResult} type="QC" />
    <p className="QcRuleTestModal__ResultStatus">
      {qcStatus || "-"}
    </p>
  </div>
);

const currentAiQcColumn = {
  title: "Current AI QC",
  key: "current_ai_qc",
  width: AI_QC_COLUMN_WIDTH,
  render: (_, record) => renderAiQcCell(
    record.current_ml_qc_result ?? record.ml_qc_result,
    record.current_ml_qc_status ?? record.ml_qc_status,
  ),
};

const newAiQcColumn = {
  title: "New AI QC",
  key: "new_ai_qc",
  width: AI_QC_COLUMN_WIDTH,
  render: (_, record) => renderAiQcCell(
    record.new_ml_qc_result,
    record.new_ml_qc_status,
  ),
};

const DEFAULT_MODAL_TITLE = "Test ML Rule";

export default function QcRuleTestModal({
  open,
  onCancel,
  ruleData,
  title = DEFAULT_MODAL_TITLE,
}) {
  const isMounted = useRef(true);
  const { setNewAbortController, clearAbortController, abortController } = useAbortController();

  const [step, setStep] = useState("select");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState(defaultQcRuleTestDateRange);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [testCategories, setTestCategories] = useState([defaultCategoryOption]);
  const [customerList, setCustomerList] = useState([defaultCustomerOption]);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkLoading, setCheckLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [resultRows, setResultRows] = useState([]);
  const [selectedRowsByKey, setSelectedRowsByKey] = useState(() => new Map());
  const [paginationConfig, setPaginationConfig] = useState({
    current: 1,
    pageSize: defaultPageSize,
    total: 0,
    pageSizeOptions: ["10", "25", "50", "100"],
  });

  const selectedRowKeys = useMemo(
    () => Array.from(selectedRowsByKey.keys()),
    [selectedRowsByKey],
  );

  const selectedRowsList = useMemo(
    () => Array.from(selectedRowsByKey.entries()),
    [selectedRowsByKey],
  );

  const clearSelection = useCallback(() => {
    setSelectedRowsByKey(new Map());
  }, []);

  const removeSelectedRow = useCallback((rowKey) => {
    setSelectedRowsByKey((prev) => {
      if (!prev.has(rowKey)) return prev;
      const next = new Map(prev);
      next.delete(rowKey);
      return next;
    });
  }, []);

  const updateSelectionForCurrentPage = useCallback((keys, currentPageRows) => {
    setSelectedRowsByKey((prev) => {
      const next = new Map(prev);
      const currentPageKeys = currentPageRows.map(getRowKey);
      currentPageKeys.forEach((key) => {
        if (!keys.includes(key)) {
          next.delete(key);
        }
      });

      let limitReached = false;
      currentPageRows.forEach((row) => {
        const key = getRowKey(row);
        if (!keys.includes(key) || next.has(key)) return;
        if (next.size >= MAX_SELECTED_ROWS) {
          limitReached = true;
          return;
        }
        next.set(key, row);
      });

      if (limitReached) {
        toast.warn(`You can select up to ${MAX_SELECTED_ROWS} tests.`);
      }

      return next;
    });
  }, []);

  const toggleRowSelection = useCallback((record) => {
    const rowKey = getRowKey(record);
    setSelectedRowsByKey((prev) => {
      const next = new Map(prev);
      if (next.has(rowKey)) {
        next.delete(rowKey);
      } else {
        if (next.size >= MAX_SELECTED_ROWS) {
          toast.warn(`You can select up to ${MAX_SELECTED_ROWS} tests.`);
          return prev;
        }
        next.set(rowKey, record);
      }
      return next;
    });
  }, []);

  const fetchTestCategories = async () => {
    const { success, results: categories, message } = await fetchTestCategory();
    if (!isMounted.current) return;
    if (success) {
      const newCategoryList = [
        defaultCategoryOption,
        ...(categories ?? []).map((item) => ({
          value: item.id,
          label: item.test_category_name,
        })),
      ];
      setTestCategories(newCategoryList);
    } else {
      toast.error(message);
    }
  };

  const fetchCustomerList = async () => {
    setCustomerLoading(true);
    const response = await getSearchOptions();
    if (!isMounted.current) return;
    let result = [defaultCustomerOption];
    if (response?.success) {
      result = [
        defaultCustomerOption,
        ...(response.list ?? [])
          .filter((item) => item?.id != null)
          .map((item) => ({
            value: item.id,
            label: item.customer_id,
          })),
      ];
      setCustomerList(result);
    } else {
      toast.info(response?.message || "Failed to load customers.");
    }
    setCustomerLoading(false);
  };

  const fetchLogs = useCallback(async ({
    page = paginationConfig.current,
    pageSize = paginationConfig.pageSize,
    searchValue = search,
    categoryId = selectedCategory,
    customerPk = selectedCustomer,
    range = dateRange,
  } = {}) => {
    const newAbortController = setNewAbortController();
    setLoading(true);
    setResults([]);
    setPaginationConfig((prev) => ({
      ...prev,
      current: page,
      pageSize,
    }));

    const params = {
      page,
      page_size: pageSize,
    };

    const formattedDateRange = formatAntdDateRange(range);
    if (formattedDateRange) {
      params.date_range = formattedDateRange;
    }

    const trimmedSearch = (searchValue || "").trim();
    if (trimmedSearch) {
      params.search = trimmedSearch;
    }
    if (categoryId !== "" && categoryId != null) {
      params.test_category_id = categoryId;
    }
    if (customerPk !== "" && customerPk != null) {
      params.customer_pk = customerPk;
    }

    try {
      const response = await getWoInfoLogs(params, newAbortController.signal);
      if (newAbortController !== abortController.current) return;

      if (response.success) {
        setResults(Array.isArray(response.results) ? response.results : []);
        setPaginationConfig((prev) => ({
          ...prev,
          current: response.page ?? page,
          pageSize: response.page_size ?? pageSize,
          total: response.total_count ?? 0,
        }));
      } else if (!response.canceled) {
        toast.error(response.message || "Failed to fetch workorder info logs");
        setResults([]);
        setPaginationConfig((prev) => ({ ...prev, total: 0 }));
      }
    } finally {
      const isLatest = newAbortController === abortController.current;
      if (isLatest) {
        clearAbortController();
        setLoading(false);
      }
    }
  }, [
    abortController,
    clearAbortController,
    dateRange,
    paginationConfig,
    search,
    selectedCategory,
    selectedCustomer,
    setNewAbortController,
  ]);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    setStep("select");
    clearSelection();
    setPaginationConfig((prev) => ({
      ...prev,
      current: 1,
      pageSize: defaultPageSize,
      total: 0,
    }));
    fetchTestCategories();
    fetchCustomerList();
  }, [open, clearSelection]);

  useEffect(() => {
    if (!open || step !== "select") return;
    fetchLogs({
      page: 1,
      pageSize: paginationConfig.pageSize,
      searchValue: search,
      categoryId: selectedCategory,
      customerPk: selectedCustomer,
      range: dateRange,
    });
  }, [open, search, selectedCategory, selectedCustomer, dateRange]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (value) => {
    const trimmedValue = (value || "").trim();
    if (trimmedValue === search) {
      fetchLogs({
        page: 1,
        pageSize: paginationConfig.pageSize,
        searchValue: trimmedValue,
        categoryId: selectedCategory,
        customerPk: selectedCustomer,
        range: dateRange,
      });
      return;
    }
    setSearch(trimmedValue);
  };

  const handlePageChange = (page, pageSize) => {
    if (checkLoading) return;
    const isPageSizeChange = pageSize !== paginationConfig.pageSize;
    const nextPage = isPageSizeChange ? 1 : page;
    fetchLogs({
      page: nextPage,
      pageSize,
      searchValue: search,
      categoryId: selectedCategory,
      customerPk: selectedCustomer,
      range: dateRange,
    });
  };

  const handleRowSelectionChange = useCallback((keys) => {
    if (checkLoading) return;
    updateSelectionForCurrentPage(keys, results);
  }, [checkLoading, results, updateSelectionForCurrentPage]);

  const handleRowClick = useCallback((record) => ({
    onClick: (event) => {
      if (checkLoading) return;
      if (
        event.target.closest(".ant-checkbox-wrapper")
        || event.target.closest(".ant-table-selection-column")
      ) {
        return;
      }
      toggleRowSelection(record);
    },
    style: { cursor: checkLoading ? "default" : "pointer" },
  }), [toggleRowSelection, checkLoading]);

  const handleCheckRule = async () => {
    const tisPks = Array.from(selectedRowsByKey.values())
      .map((row) => row.tests_in_sample_PK)
      .filter((pk) => pk != null);

    if (!tisPks.length) {
      toast.warn("Select at least one test.");
      return;
    }

    setCheckLoading(true);
    try {
      const response = await detectErrorRuleCheck({ tis_pks: tisPks });

      if (response.success) {
        const resultMap = new Map(
          (Array.isArray(response.result) ? response.result : []).map((item) => [item.tis_pk, item]),
        );
        const merged = Array.from(selectedRowsByKey.values()).map((row) => {
          const apiResult = resultMap.get(row.tests_in_sample_PK) || {};
          return {
            ...row,
            current_ml_qc_result: row.ml_qc_result,
            current_ml_qc_status: row.ml_qc_status,
            new_ml_qc_result: apiResult.ml_qc_result,
            new_ml_qc_status: apiResult.ml_qc_status,
          };
        });
        setResultRows(merged);
        setStep("results");
      } else {
        toast.error(response.message || "Failed to check rule");
      }
    } finally {
      setCheckLoading(false);
    }
  };

  const handleBack = () => {
    setStep("select");
    setResultRows([]);
  };

  const selectColumns = useMemo(() => ([
    ...baseColumns,
    currentAiQcColumn,
  ]), []);

  const resultColumns = useMemo(() => ([
    ...baseColumns,
    currentAiQcColumn,
    newAiQcColumn,
  ]), []);

  const modalTitle = title;

  const renderFooter = () => {
    if (step === "results") {
      return (
        <Flex justify="flex-end" gap={8} className="QcRuleTestModal__Footer">
          <Button onClick={handleBack}>
            Back
          </Button>
          <Button onClick={onCancel}>
            Close
          </Button>
        </Flex>
      );
    }

    return (
      <Flex justify="flex-end" gap={8} className="QcRuleTestModal__Footer">
        <Button onClick={onCancel}>
          Close
        </Button>
        <Button
          type="primary"
          loading={checkLoading}
          disabled={checkLoading || selectedRowsByKey.size === 0}
          onClick={handleCheckRule}
        >
          Check Rule
        </Button>
      </Flex>
    );
  };

  return (
    <Modal
      centered
      title={modalTitle}
      width={1400}
      open={open}
      onCancel={onCancel}
      rootClassName="QcRuleTestModal"
      footer={renderFooter()}
      destroyOnClose
    >
      <div className="QcRuleTestModal__BodyContent">
        <QcRuleTestDetailSection ruleData={ruleData} />
        {step === "select" && (
          <>
            <div className="QcRuleTestModal__Filters">
              <div className="QcRuleTestModal__Field">
                <label>Search</label>
                <Input.Search
                  allowClear
                  disabled={checkLoading}
                  placeholder="Search by WO, Genista ID, Test"
                  className="QcRuleTestModal__Search"
                  value={searchInput}
                  onChange={(e) => {
                    const { value } = e.target;
                    setSearchInput(value);
                    if (value === "") {
                      handleSearch("");
                    }
                  }}
                  onSearch={handleSearch}
                />
              </div>
              <div className="QcRuleTestModal__Field">
                <label>Workorder Date Range</label>
                <RangePicker
                  allowClear={false}
                  disabled={checkLoading}
                  className="QcRuleTestModal__DateRange"
                  format="MM/DD/YYYY"
                  value={dateRange}
                  onChange={(value) => {
                    setDateRange(value || defaultQcRuleTestDateRange());
                    setPaginationConfig((prev) => ({ ...prev, current: 1 }));
                  }}
                />
              </div>
              <div className="QcRuleTestModal__Field">
                <label>Test Category</label>
                <Select
                  allowClear
                  showSearch
                  disabled={checkLoading}
                  className="QcRuleTestModal__Select"
                  popupClassName="QcRuleTestModal__SelectMenu"
                  options={testCategories}
                  value={selectedCategory}
                  onSelect={(value) => {
                    setSelectedCategory(value);
                    setPaginationConfig((prev) => ({ ...prev, current: 1 }));
                  }}
                  onClear={() => {
                    setSelectedCategory("");
                    setPaginationConfig((prev) => ({ ...prev, current: 1 }));
                  }}
                  filterOption={(inputValue, option) => (
                    String(option?.label ?? "").toLowerCase().includes(inputValue.toLowerCase())
                  )}
                  listHeight={400}
                />
              </div>
              <div className="QcRuleTestModal__Field">
                <label>Customer</label>
                <Select
                  allowClear
                  showSearch
                  disabled={checkLoading}
                  className="QcRuleTestModal__Select"
                  popupClassName="QcRuleTestModal__SelectMenu"
                  loading={customerLoading}
                  options={customerList}
                  value={selectedCustomer}
                  onSelect={(value) => {
                    setSelectedCustomer(value);
                    setPaginationConfig((prev) => ({ ...prev, current: 1 }));
                  }}
                  onClear={() => {
                    setSelectedCustomer("");
                    setPaginationConfig((prev) => ({ ...prev, current: 1 }));
                  }}
                  filterOption={(inputValue, option) => (
                    String(option?.label ?? "").toLowerCase().includes(inputValue.toLowerCase())
                  )}
                  listHeight={400}
                />
              </div>
            </div>

            <div className="QcRuleTestModal__SelectionSection">
              <Flex
                align="center"
                justify="space-between"
                className="QcRuleTestModal__SelectionHeader"
              >
                <span className="QcRuleTestModal__SelectionCount">
                  {selectedRowsByKey.size}
                  {" / "}
                  {MAX_SELECTED_ROWS}
                  {" "}
                  test
                  {selectedRowsByKey.size === 1 ? "" : "s"}
                  {" "}
                  selected
                </span>
                <Button
                  type="link"
                  className="QcRuleTestModal__ClearSelection"
                  disabled={checkLoading || selectedRowsByKey.size === 0}
                  onClick={clearSelection}
                >
                  Clear
                </Button>
              </Flex>
              {selectedRowsList.length > 0 && (
                <div className="QcRuleTestModal__SelectionCards">
                  {selectedRowsList.map(([rowKey, row]) => (
                    <div key={rowKey} className="QcRuleTestModal__SelectionCard">
                      <span className="QcRuleTestModal__SelectionCardLabel" title={formatSelectedRowLabel(row)}>
                        {formatSelectedRowLabel(row)}
                      </span>
                      <Button
                        type="text"
                        size="small"
                        disabled={checkLoading}
                        className="QcRuleTestModal__SelectionCardRemove"
                        icon={<CloseOutlined />}
                        aria-label="Remove selected row"
                        onClick={() => removeSelectedRow(rowKey)}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Table
              rowKey={getRowKey}
              dataSource={results}
              columns={selectColumns}
              loading={loading}
              bordered
              className="QcRuleTestModal__Table"
              scroll={{ x: "max-content", y: 400 }}
              onRow={handleRowClick}
              rowSelection={{
                type: "checkbox",
                selectedRowKeys,
                onChange: handleRowSelectionChange,
                getCheckboxProps: (record) => {
                  const rowKey = getRowKey(record);
                  const isSelected = selectedRowsByKey.has(rowKey);
                  return {
                    disabled: checkLoading || (!isSelected && selectedRowsByKey.size >= MAX_SELECTED_ROWS),
                  };
                },
              }}
              pagination={{
                ...paginationConfig,
                showSizeChanger: true,
                disabled: checkLoading,
                onChange: handlePageChange,
              }}
            />
          </>
        )}

        {step === "results" && (
          <Table
            rowKey={getRowKey}
            dataSource={resultRows}
            columns={resultColumns}
            bordered
            className="QcRuleTestModal__Table"
            scroll={{ x: "max-content", y: 400 }}
            pagination={false}
          />
        )}
      </div>
    </Modal>
  );
}
